import { ArrowLeft, Check, KeyRound, Loader2, ShieldAlert, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { chamarApi } from '@/nucleo/auth/cliente-api';
import { MENSAGEM_GENERICA } from '@/nucleo/auth/mensagens';
import { Alert, AlertDescription, AlertTitle } from '@/ui/base/alert';
import { Button } from '@/ui/base/button';
import { LayoutAutenticacao } from './LayoutAutenticacao';

interface Estado {
  titulo: string;
  descricao: string;
  fase: string;
  carregando: boolean;
  pedido: { valido: boolean; segredoExposto: false };
  requisitos: string[];
  formulario: { proprietario: 'KEYCLOAK'; senhaNoTeg: false; instrucao: string };
  erro: { titulo: string; mensagem: string; correlacao: string | null } | null;
  confirmacao: { titulo: string; mensagem: string; sessoesEncerradas: number } | null;
  comandos: Array<{
    verbo: 'validar_pedido' | 'redefinir_credencial';
    rotulo: string;
    habilitado: boolean;
  }>;
  navegacao: Array<{ rotulo: string; caminho: '/login' | '/esqueci-minha-senha' }>;
}
interface Envelope {
  contract_version: string;
  screen: 'redefinir-senha';
  event: { verb: string; outcome: string; at: string };
  state: Estado;
}
const NAMESPACE = '/bff/v1/redefinir-senha';

export function RedefinirSenhaPage() {
  const [parametros] = useSearchParams();
  const operacao = parametros.get('operacao');
  const [estado, setEstado] = useState<Estado | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [falha, setFalha] = useState<string | null>(null);
  useEffect(() => {
    const caminho = operacao ? `${NAMESPACE}/verbs/validar_pedido` : `${NAMESPACE}/state`;
    chamarApi<Envelope>(caminho, {
      metodo: operacao ? 'POST' : 'GET',
      corpo: operacao ? { operacao } : undefined,
      publica: true,
    })
      .then((e) => setEstado(e.state))
      .catch(() => setFalha(MENSAGEM_GENERICA))
      .finally(() => setCarregando(false));
  }, [operacao]);
  async function executar() {
    if (!operacao) return;
    setCarregando(true);
    try {
      const e = await chamarApi<Envelope>(`${NAMESPACE}/verbs/redefinir_credencial`, {
        metodo: 'POST',
        corpo: { operacao },
        publica: true,
      });
      setEstado(e.state);
    } catch {
      setFalha(MENSAGEM_GENERICA);
    } finally {
      setCarregando(false);
    }
  }
  const comando = estado?.comandos.find((c) => c.verbo === 'redefinir_credencial');
  return (
    <LayoutAutenticacao
      titulo={estado?.titulo ?? 'Definir nova senha'}
      descricao={estado?.descricao ?? 'Validando o retorno seguro…'}
    >
      <main className="space-y-5" aria-busy={carregando}>
        {carregando && !estado && (
          <div role="status" className="flex gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Validando pedido…
          </div>
        )}
        {estado?.confirmacao && (
          <Alert className="border-success/40 bg-success/10">
            <ShieldCheck className="h-4 w-4 text-success" />
            <AlertTitle>{estado.confirmacao.titulo}</AlertTitle>
            <AlertDescription>{estado.confirmacao.mensagem}</AlertDescription>
          </Alert>
        )}
        {(estado?.erro || falha) && (
          <Alert variant="destructive">
            <ShieldAlert className="h-4 w-4" />
            <AlertTitle>{estado?.erro?.titulo ?? 'Não foi possível validar'}</AlertTitle>
            <AlertDescription>{estado?.erro?.mensagem ?? falha}</AlertDescription>
          </Alert>
        )}
        {!estado?.confirmacao && (
          <>
            <Alert className="border-warning/40 bg-warning/10">
              <ShieldAlert className="h-4 w-4 text-warning" />
              <AlertTitle>A senha fica no provedor</AlertTitle>
              <AlertDescription>
                {estado?.formulario.instrucao ?? 'Abra o link recebido por e-mail para continuar.'}
              </AlertDescription>
            </Alert>
            <section aria-labelledby="requisitos" className="space-y-3">
              <h2 id="requisitos" className="text-sm font-semibold">
                O que a nova senha precisa ter
              </h2>
              <ul className="space-y-2">
                {(estado?.requisitos ?? []).map((r) => (
                  <li key={r} className="flex gap-2 text-sm text-muted-foreground">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </section>
          </>
        )}
        {comando?.habilitado && (
          <Button onClick={executar} disabled={carregando} size="lg" className="h-12 w-full">
            {carregando ? 'Confirmando…' : comando.rotulo}
          </Button>
        )}
        {estado?.confirmacao && (
          <Button asChild size="lg" className="h-12 w-full">
            <Link to="/login">Entrar no sistema</Link>
          </Button>
        )}
        {(estado?.navegacao ?? [])
          .filter((n) => !estado?.confirmacao || n.caminho === '/login')
          .map((n) => (
            <div key={n.caminho} className="text-center text-sm">
              <Link
                className="inline-flex items-center gap-1.5 text-muted-foreground hover:underline"
                to={n.caminho}
              >
                {n.caminho === '/login' ? (
                  <ArrowLeft className="h-3.5 w-3.5" />
                ) : (
                  <KeyRound className="h-4 w-4" />
                )}
                {n.rotulo}
              </Link>
            </div>
          ))}
      </main>
    </LayoutAutenticacao>
  );
}
export default RedefinirSenhaPage;
